import { useCallback, useEffect, useMemo, useReducer, useRef, type ReactNode } from 'react';

import { COMMERCE } from '@/config/site';
import { getBySku } from '@/data/products';
import { sumCents, toCents, type Cents } from '@/lib/money';
import { readStorage, writeStorage } from '@/lib/storage';

import {
  CartActionsContext,
  CartStateContext,
  CartSummaryContext,
  type CartActions,
} from './cart.context';
import { cartReducer, clampQuantity, initialCartState, sanitiseLines } from './cart.reducer';
import type {
  AddToCartResult,
  CartLine,
  CartState,
  CartSummary,
  ResolvedCartLine,
} from './cart.types';

const FREE_SHIPPING_CENTS = toCents(COMMERCE.freeShippingThreshold);
const DELIVERY_FEE_CENTS = toCents(COMMERCE.deliveryFee);

function lazyInit(): CartState {
  return {
    ...initialCartState,
    lines: sanitiseLines(readStorage(COMMERCE.cartStorageKey, [])),
  };
}

function buildSummary(cartLines: CartLine[]): CartSummary {
  const lines: ResolvedCartLine[] = [];

  for (const line of cartLines) {
    const entry = getBySku(line.sku);
    // SKU removido do catálogo entre sessões simplesmente desaparece.
    if (!entry) continue;

    lines.push({
      sku: line.sku,
      quantity: line.quantity,
      product: entry.product,
      variant: entry.variant,
      lineTotalCents: entry.variant.priceCents * line.quantity,
      isUnavailable: entry.variant.stock === 'out-of-stock',
    });
  }

  // Item esgotado não entra na conta: cobrar por algo que não será entregue
  // faz o total do site divergir do que a equipe confirma no WhatsApp.
  const sellable = lines.filter((line) => !line.isUnavailable);
  const unavailableLines = lines.filter((line) => line.isUnavailable);

  const subtotalCents: Cents = sumCents(sellable.map((line) => line.lineTotalCents));
  const itemCount = sellable.reduce((sum, line) => sum + line.quantity, 0);

  const isEligibleForFreeShipping = subtotalCents >= FREE_SHIPPING_CENTS;
  const deliveryFeeCents =
    subtotalCents === 0 || isEligibleForFreeShipping ? 0 : DELIVERY_FEE_CENTS;

  return {
    lines,
    itemCount,
    subtotalCents,
    deliveryFeeCents,
    totalCents: subtotalCents + deliveryFeeCents,
    isEligibleForFreeShipping,
    amountToFreeShippingCents: Math.max(0, FREE_SHIPPING_CENTS - subtotalCents),
    freeShippingProgress: Math.min(1, subtotalCents / FREE_SHIPPING_CENTS),
    unavailableLines,
    canCheckout: sellable.length > 0 && unavailableLines.length === 0,
  };
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(cartReducer, undefined, lazyInit);

  // Guarda a última gravação para distinguir a nossa própria escrita de uma
  // feita por outra aba.
  const lastWritten = useRef<string>('');

  useEffect(() => {
    lastWritten.current = JSON.stringify(state.lines);
    writeStorage(COMMERCE.cartStorageKey, state.lines);
  }, [state.lines]);

  // Sincroniza abas: sem isso, quem tem duas abas abertas vê a última que
  // mexeu no carrinho sobrescrever a outra.
  useEffect(() => {
    function onStorage(event: StorageEvent) {
      if (event.key !== COMMERCE.cartStorageKey || event.newValue === null) return;
      if (event.newValue === lastWritten.current) return;

      try {
        dispatch({ type: 'replaceLines', lines: sanitiseLines(JSON.parse(event.newValue)) });
      } catch {
        // Valor ilegível de outra aba — mantém o carrinho atual.
      }
    }

    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Espelho do estado para `addItem` calcular o efeito sem depender do
  // fechamento (closure) da renderização anterior.
  const stateRef = useRef(state);
  stateRef.current = state;

  /**
   * Adiciona e informa o que aconteceu. O reducer trunca no limite por item em
   * silêncio; sem este retorno a UI comemoraria "Adicionado!" depois de somar
   * zero unidade.
   */
  const addItem = useCallback((sku: string, quantity = 1): AddToCartResult => {
    const entry = getBySku(sku);

    if (!entry) return { status: 'unknown-sku', addedQuantity: 0, resultingQuantity: 0 };

    if (entry.variant.stock === 'out-of-stock') {
      return { status: 'unavailable', addedQuantity: 0, resultingQuantity: 0 };
    }

    const current = stateRef.current.lines.find((line) => line.sku === sku)?.quantity ?? 0;
    const resulting = clampQuantity(current + quantity);
    const added = resulting - current;

    dispatch({ type: 'add', sku, quantity });

    return {
      status: added < quantity ? 'clamped' : 'added',
      addedQuantity: added,
      resultingQuantity: resulting,
    };
  }, []);

  const actions = useMemo<CartActions>(
    () => ({
      addItem,
      setQuantity: (sku, quantity) => dispatch({ type: 'setQuantity', sku, quantity }),
      removeItem: (sku) => dispatch({ type: 'remove', sku }),
      removeUnavailable: () => dispatch({ type: 'removeUnavailable' }),
      clearCart: () => dispatch({ type: 'clear' }),
      openCart: () => dispatch({ type: 'setOpen', isOpen: true }),
      closeCart: () => dispatch({ type: 'setOpen', isOpen: false }),
      setCartOpen: (isOpen) => dispatch({ type: 'setOpen', isOpen }),
    }),
    [addItem],
  );

  // Depende só das linhas — abrir a gaveta não recalcula os totais.
  const summary = useMemo(() => buildSummary(state.lines), [state.lines]);

  return (
    <CartActionsContext.Provider value={actions}>
      <CartStateContext.Provider value={state}>
        <CartSummaryContext.Provider value={summary}>{children}</CartSummaryContext.Provider>
      </CartStateContext.Provider>
    </CartActionsContext.Provider>
  );
}
